import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from '@tanstack/react-router';
import { useWindowDimensions } from 'hooks/useWindowDimensions';
import { Steps, type Step } from 'intro.js-react';

import OnboardingSteps from './OnboardingSteps';
import {
  adminFleetActions,
  adminHistories,
  adminUserActions,
  chargeCenterActions,
  chargeCenterActionsConnectors,
  chargeCenterIndexActions,
  fleetActions,
  fleetActionsSummary,
  historicalUptimeActions,
  remoteManagementActions,
  remoteManagementIndexActions,
} from './utils';
import { scrollToTooltipFixRevert } from './utils/tooltip-scrolling';

import 'intro.js/introjs.css';
import './styles.scss';

import { ampli } from '@/ampli';

const stepsOptions = {
  skipLabel: 'SALTAR',
  nextLabel: 'SIGUIENTE',
  prevLabel: 'ATRÁS',
  doneLabel: 'FINALIZAR',
  exitOnOverlayClick: false,
  hidePrev: true,
  disableInteraction: true,
  scrollToElement: true,
  scrollTo: 'tooltip',
  autoPosition: true,
};

interface OnboardingContextValues {
  changeCurrentOnboarding: (currentOnboarding: string) => void;
  startOnboarding: () => void;
  setActionCallbacks: (newCallbacks: Array<() => void>) => void;
  setRemoveActionCallbacks: (newCallbacks: () => void) => void;
  setActionProps: (newActionProps: unknown) => void;
  enabled: boolean;
}

export const OnboardingContext = createContext<OnboardingContextValues>({
  changeCurrentOnboarding: () => {},
  startOnboarding: () => {},
  setActionCallbacks: () => {},
  setRemoveActionCallbacks: () => {},
  setActionProps: () => {},
  enabled: false,
});

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboardingContext must be used within a OnboardingProvider');
  }
  return context;
};

const OnboardingProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation();
  const [stepsApi, setStepsApi] = useState<Steps>();
  const [currentOnboarding, setCurrentOnboarding] = useState('welcome');
  const [steps, setSteps] = useState<Step[]>(OnboardingSteps[currentOnboarding]);
  const [enabled, setEnabled] = useState(false);
  const [onboardingVisited, setOnboardingVisited] = useState<
    Record<string, string | boolean | null>
  >({
    welcome: localStorage.getItem('onboarding_welcome'),
    charge_center_index: localStorage.getItem('onboarding_charge_center_index'),
    charge_center_details: localStorage.getItem('onboarding_charge_center_details'),
    charge_center_details_connectors: localStorage.getItem(
      'onboarding_charge_center_details_connectors'
    ),
    remote_management_index: localStorage.getItem('onboarding_remote_management_index'),
    remote_management_details: localStorage.getItem('onboarding_remote_management_details'),
    fleet_map: localStorage.getItem('onboarding_fleet_map'),
    fleet_summary: localStorage.getItem('onboarding_fleet_summary'),
    historical_chargepoint_uptime: localStorage.getItem('onboarding_historical_chargepoint_uptime'),
    admin_index: localStorage.getItem('onboarding_admin_index'),
    admin_users: localStorage.getItem('onboarding_admin_users'),
    admin_chargepoint_status: localStorage.getItem('onboarding_admin_chargepoint_status'),
    admin_transaction_history: localStorage.getItem('onboarding_admin_transaction_history'),
    admin_fleet_management: localStorage.getItem('onboarding_admin_fleet_management'),
    admin_trip_history: localStorage.getItem('onboarding_admin_trip_history'),
  });
  const actionCallbacks = useRef<Array<() => void> | null>(null);
  const removeActionCallbacks = useRef<(() => void) | null>(null);
  // TODO: Fix this implementation
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const actionProps = useRef<any>(null);
  const { width } = useWindowDimensions();

  const changeCurrentOnboarding = (currentOnboarding: string) => {
    setCurrentOnboarding(currentOnboarding);
  };

  const startOnboarding = useCallback(() => {
    if (actionCallbacks.current) actionCallbacks.current.forEach((callback) => callback());
    setSteps(OnboardingSteps[currentOnboarding]);
    if (width > 700) setEnabled(true);
  }, [currentOnboarding, width]);

  const endOnboarding = useCallback(() => {
    scrollToTooltipFixRevert();
    removeActionCallbacks?.current?.();
    clearActionProps();
    clearActionCallbacks();
    clearRemoveActionCallbacks();
    setEnabled(false);
    setOnboardingVisited((onboardingVisited) => ({
      ...onboardingVisited,
      [currentOnboarding]: true,
    }));
    localStorage.setItem(`onboarding_${currentOnboarding}`, 'true');
  }, [currentOnboarding]);

  const clearActionCallbacks = () => {
    actionCallbacks.current = null;
  };

  const clearRemoveActionCallbacks = () => {
    removeActionCallbacks.current = null;
  };

  const setActionProps = (newActionProps: unknown) => {
    actionProps.current = newActionProps;
  };

  const clearActionProps = () => {
    actionProps.current = null;
  };

  const setActionCallbacks = (newCallbacks: Array<() => void>) => {
    actionCallbacks.current = newCallbacks;
  };

  const setRemoveActionCallbacks = (newCallbacks: () => void) => {
    removeActionCallbacks.current = newCallbacks;
  };

  const onChange = (nextStepIndex: number) => {
    if (pathname.includes('/charge-center') && !pathname.includes('grid-connections'))
      chargeCenterIndexActions(nextStepIndex);
    if (
      pathname.includes('/charge-center/historical-uptime') ||
      pathname.includes('/analytics') ||
      pathname.includes('/analytics/historical-uptime')
    )
      historicalUptimeActions(nextStepIndex, actionProps.current);
    if (pathname.includes('/charge-center/grid-connections/') && pathname.includes('charge-points'))
      chargeCenterActions(nextStepIndex, actionProps.current);
    if (pathname.includes('/charge-center/grid-connections/') && pathname.includes('connectors')) {
      chargeCenterActionsConnectors(nextStepIndex, actionProps.current);
    }
    if (pathname.includes('/remote-management') && !pathname.includes('grid-connections'))
      remoteManagementIndexActions(nextStepIndex);
    if (pathname.includes('/remote-management/grid-connections'))
      remoteManagementActions(nextStepIndex, actionProps.current);
    if (pathname.includes('/fleet/map')) fleetActions(nextStepIndex, actionProps.current);
    if (pathname.includes('/fleet/summary'))
      fleetActionsSummary(nextStepIndex, actionProps.current);
    if (pathname.includes('/admin/users')) adminUserActions(nextStepIndex, actionProps.current);
    if (pathname.includes('/admin/fleet-management'))
      adminFleetActions(nextStepIndex, actionProps.current);
    if (pathname.includes('/admin/fleet-trip-history/'))
      adminHistories(nextStepIndex, actionProps.current);
    if (pathname.includes('/admin/charge-point-management/status/'))
      adminHistories(nextStepIndex, actionProps.current);
    if (pathname.includes('/admin/assets-transactions'))
      adminHistories(nextStepIndex, actionProps.current);
    if (stepsApi && nextStepIndex && nextStepIndex < steps.length - 1)
      stepsApi.updateStepElement(nextStepIndex);
  };

  const handleSetStepsApi = (stepsApi: Steps) => {
    setStepsApi(stepsApi);
  };

  useEffect(() => {
    if (!onboardingVisited[currentOnboarding]) {
      startOnboarding();
      ampli.onboardingModalViewed({
        'onboarding screen path': window.location.pathname,
        'onboarding type': 'automatic',
      });
    }
  }, [currentOnboarding, onboardingVisited, startOnboarding]);

  return (
    <OnboardingContext.Provider
      value={{
        changeCurrentOnboarding,
        startOnboarding,
        setActionCallbacks,
        setRemoveActionCallbacks,
        setActionProps,
        enabled,
      }}
    >
      {children}
      <Steps
        enabled={enabled}
        ref={handleSetStepsApi}
        onBeforeChange={onChange}
        initialStep={0}
        options={stepsOptions}
        steps={steps}
        onExit={endOnboarding}
      />
    </OnboardingContext.Provider>
  );
};

export default OnboardingProvider;
